<template>
	<v-dialog v-model="showDialog" max-width="700">
		<v-card class="pb-5">
			<v-toolbar color="primary" dark flat>
				<v-toolbar-title> Source Data Extraction Validation </v-toolbar-title>
			</v-toolbar>
			<v-card-text
				class="pa-5 d-flex flex-sm-nowrap align-center justify-center validation-header-text"
			>
				<v-icon v-if="isValid" class="mr-3" color="green" size="x-large">
					mdi-check-circle
				</v-icon>
				<v-icon v-else class="mr-3" color="red" size="x-large">
					mdi-alert-circle
				</v-icon>
				{{ validationMessage }}
			</v-card-text>

			<data-entry-validation-table
				v-if="!isValid"
				:validation-data="validationData"
			></data-entry-validation-table>

			<v-alert
				v-if="saveWasRejected"
				class="mx-5 mt-2 text-body-2 bg-valence-negative-tint text-valence-negative-ink"
				density="compact"
			>
				This extraction was not saved. Your work is still on this page. Fix the
				record above and save again.
			</v-alert>

			<v-alert
				v-if="confirmingReload"
				class="mx-5 mt-2 text-body-2 bg-valence-negative-tint text-valence-negative-ink"
				density="compact"
			>
				Reloading discards every change you made since your last successful
				save, including the record listed above. This cannot be undone.
			</v-alert>

			<v-card-actions>
				<v-spacer />
				<v-btn
					v-if="isValid"
					class="mr-3 bg-grey"
					elevation="2"
					@click="downloadDactylizerJson"
				>
					Download JSON
				</v-btn>
				<v-btn
					v-if="showDataPointSelectionButton()"
					class="mr-3 bg-grey"
					elevation="2"
					@click="selectDataPoint"
				>
					{{ fixDataLabel() }}
				</v-btn>
				<v-btn
					v-if="saveWasRejected && !confirmingReload"
					class="mr-3 bg-valence-negative-tint text-valence-negative-ink"
					elevation="2"
					@click="confirmingReload = true"
				>
					Discard changes and reload
				</v-btn>
				<v-btn
					v-if="confirmingReload"
					class="mr-3 bg-valence-negative-tint text-valence-negative-ink"
					elevation="2"
					@click="discardChangesAndReload"
				>
					Yes, discard my changes
				</v-btn>
				<v-btn class="bg-grey" elevation="2" @click="closeValidationDialog">
					{{ closeLabel }}
				</v-btn>
				<v-spacer />
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import DataEntryValidationTable from "@/pages/DataEntry/DataEntryValidationTable";
import { mapActions } from "pinia";
import { useMainStore } from "@/store";
import { useLDFlag } from "launchdarkly-vue-client-sdk";

export default {
	name: "DataEntryValidationDialog",

	components: { DataEntryValidationTable },

	props: {
		modelValue: Boolean,
		validationData: { type: Object, default: () => null },
		// eslint-disable-next-line vue/require-prop-types
		moduleWindow: {
			required: true,
		},
		sdeUuid: {
			type: String,
			required: true,
		},
		done: {
			type: Boolean,
			required: true,
		},
		triggeredBySave: {
			type: Boolean,
			required: true,
		},
	},
	emits: ["update:modelValue"],

	setup() {
		const preventInvalidSavesEnabled = useLDFlag("prevent-invalid-wpd-saves");
		return {
			preventInvalidSavesEnabled,
		};
	},

	data() {
		return {
			confirmingReload: false,
		};
	},

	computed: {
		showDialog: {
			get() {
				return this.modelValue;
			},
			set(newValue) {
				this.$emit("update:modelValue", newValue);
			},
		},
		isValid() {
			return this.validationData && this.validationData.status === "success";
		},
		validationMessage() {
			if (this.validationData && this.validationData.message) {
				return this.validationData.message;
			}

			return "";
		},
		supportedDataTypes() {
			return ["image", "branch", "text", "table"];
		},
		// True when `preventInvalidSaves` made the server REJECT this save, so
		// nothing the curator did since their last successful save is stored.
		// DCTL-2459: this state used to force `window.location.reload()` on any
		// dismissal, which discarded exactly the work the server had refused to
		// keep. Reloading is now an explicit, separately confirmed action, and
		// dismissing the dialog always leaves the page and its work intact.
		//
		// Keeping the page cannot leave an invalid document stored: the server
		// re-validates every save while the flag is on, so an invalid extraction
		// is refused every time. The guard is the server, not the reload.
		saveWasRejected() {
			return (
				this.preventInvalidSavesEnabled &&
				!this.done &&
				this.triggeredBySave &&
				!this.isValid
			);
		},
		closeLabel() {
			if (this.confirmingReload) return "Keep my changes";
			return this.saveWasRejected ? "Keep editing" : "Ok";
		},
	},

	watch: {
		modelValue(newValue) {
			// Reset the reload confirmation so a later failure never opens the
			// dialog already armed to discard.
			if (!newValue) {
				this.confirmingReload = false;
			}
		},
	},

	methods: {
		...mapActions(useMainStore, ["showNotification"]),
		discardChangesAndReload() {
			console.error(
				`Invalid SDE save for SDE (${this.sdeUuid}); user chose to discard unsaved changes and reload the last valid save`,
			);
			window.location.reload();
		},
		fixDataLabel() {
			switch (this.validationData?.data?.type) {
				case "branch":
					return "Add study branch";
				case "table":
					return "Edit table";
				default:
					return "View data point";
			}
		},
		// DCTL-2459: this used to exclude `saveWasRejected`, which hid the one
		// control that takes the curator to the flagged record precisely when
		// that record is the only thing blocking their save. The affordance is
		// now shown for every invalid result.
		showDataPointSelectionButton() {
			// `data` is optional on DactylizerException, and this button now
			// renders on the save-rejection path too, so reach for it safely.
			const data = this.validationData?.data;
			return (
				!this.isValid &&
				!!data &&
				this.supportedDataTypes.includes(data.type) &&
				!!data.x &&
				!!data.y
			);
		},
		closeValidationDialog() {
			this.showDialog = false;
		},
		downloadDactylizerJson() {
			this.$http
				.post(`/data-entry/${this.sdeUuid}/validate`, {
					includeJson: true,
				})
				.then((response) => {
					const data = response.data?.data;
					const blob = new Blob([JSON.stringify(data, null, 2)], {
						type: "application/json",
					});

					const url = URL.createObjectURL(blob);
					const link = document.createElement("a");
					link.href = url;
					link.download = data.source ? data.source.name : "Unknown paper";
					document.body.appendChild(link);

					link.click();

					document.body.removeChild(link);
					URL.revokeObjectURL(url);
				})
				.catch((error) => {
					this.showNotification({
						message: `Error while attempting to validate: ${error.response?.data?.message}`,
						status: "error",
					});
				});
		},
		selectDataPoint() {
			const data = this.validationData.data;
			this.moduleWindow.document.dispatchEvent(
				new CustomEvent("select-data-point", {
					detail: {
						axesName: data.axesName,
						datasetName: data.datasetName,
						file: data.file,
						page: data.page,
						x: data.x,
						y: data.y,
						type: data.type,
					},
				}),
			);

			this.showDialog = false;
		},
	},
};
</script>

<style scoped lang="scss">
.validation-header-text {
	font-size: 1.1em;
	color: rgba(0, 0, 0, 0.87) !important;
}
</style>
